import { useCallback } from 'react';
import { DepositSearchParamValues } from './props';
import { useCurrentModal } from '../../modals/useCurrentModal';
import { DepositStep, CommonModalsKey } from '../../../enums';
import { CoinType } from '../../../models';
import { Nullable } from '../../../interfaces';
import { useSearchParams } from '../../useSearchParams';
import { useNavigate } from '../../useNavigate';
import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { parseToBigInt } from '@starsoft/common/utils/parsers';

/**
 * Custom hook to manage deposit-related search parameters.
 * This hook provides functionalities to retrieve and manipulate search parameters
 * related to deposit operations, such as transaction steps, coin types, and user payment details.
 *
 * @returns {Object} An object containing the current search parameters and functions to modify them.
 */
export default function useDepositSearchParams() {
  // Retrieve the current URL search parameters
  const search: URLSearchParams = useSearchParams();
  // Get the current modal key, if any
  const modal: string | undefined = useCurrentModal(CommonModalsKey);
  // Function to navigate and update the URL
  const navigate: NavigateFunction = useNavigate();

  // Extract the transaction step from the search parameters, defaulting to DepositStep.Form
  const transactionStep: DepositStep =
    (search.get('step') as DepositStep) ?? DepositStep.Form;
  // Extract the coin type from the search parameters, defaulting to CoinType.Fiat
  const coinType: CoinType =
    (search.get('coin-type') as CoinType) ?? CoinType.Fiat;
  // Extract the method ID from the search parameters
  const searchMethodId: string | null = search.get('method-id');
  // Convert method ID to a nullable string
  const depositMethodId: Nullable<string> = searchMethodId
    ? searchMethodId
    : null;
  // Extract the coin ID from the search parameters
  const searchCoinId: string | null = search.get('coin-id');
  // Convert coin ID to a nullable string
  const coinId: Nullable<string> = searchCoinId ? searchCoinId : null;
  // Extract user payment details from the search parameters
  const userPaymentDetails: string = search.get('user-payment');

  /**
   * Function to set a specific deposit search parameter.
   * It updates the URL with the new search parameter value.
   *
   * @template K - The key of the search parameter to set.
   * @param {K} searchParamKey - The key of the search parameter.
   * @param {DepositSearchParamValues[K]} value - The value to set for the search parameter.
   *
   * @example
   * handleSetDepositSearchParams('step', DepositStep.Confirm);
   */
  const handleSetDepositSearchParams = useCallback(
    <K extends keyof DepositSearchParamValues>(
      searchParamKey: K,
      value: DepositSearchParamValues[K],
    ) => {
      if (!modal) {
        return;
      }
      search.set(searchParamKey, `${String(value?.toString())}`);
      navigate({
        search: search.toString(),
        hash: modal,
      });
    },
    [search, modal, navigate],
  );

  /**
   * Function to delete a specific deposit search parameter.
   * It removes the search parameter from the URL.
   *
   * @template K - The key of the search parameter to delete.
   * @param {K} searchParamKey - The key of the search parameter.
   *
   * @example
   * handleDeleteDepositSearchParams('coin-id');
   */
  const handleDeleteDepositSearchParams = useCallback(
    <K extends keyof DepositSearchParamValues>(searchParamKey: K) => {
      search.delete(searchParamKey);
      navigate({
        search: search.toString(),
        hash: modal,
      });
    },
    [search, modal, navigate],
  );

  // Return the current search parameters and the functions to modify them
  return {
    transactionStep,
    coinType,
    depositMethodId,
    coinId,
    userPaymentDetails,
    handleSetDepositSearchParams,
    handleDeleteDepositSearchParams,
  };
}

export type { DepositSearchParamValues };
